import type { PublicTrackHistory, TrackHistory, TrackHistoryContext } from '@shady-pines/db';
import { isNonNil } from '@shady-pines/types';
import { head, isNil } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useGlobalContextStore } from '../global-context/global-context-store';
import { useUserStore } from '../user/user-store';
import { useTrackStore } from './track-store';

const AcrCloudContextToIgnore: TrackHistoryContext[] = [
  'acr-cloud-but-low-confidence',
  'acr-cloud-but-radio-co-relevant',
] as const;

const ContextsToIgnoreForTrackHistory: TrackHistoryContext[] = [
  'radio-co-ad',
  'radio-co-show',
  ...AcrCloudContextToIgnore,
] as const;

const meetsConfidenceThreshold = (
  newMessage: TrackHistory,
  recognitionConfidenceThreshold: number,
) => {
  if (newMessage.service_identifier !== 'acr-cloud') {
    return true;
  }

  if (newMessage.acr_cloud_relevant === false) {
    return false;
  }

  return (
    isNonNil(newMessage.recognition_confidence) &&
    newMessage.recognition_confidence >= recognitionConfidenceThreshold
  );
};

const shouldPlaceInTrackHistory = (
  newMessage: TrackHistory,
  recognitionConfidenceThreshold: number,
  initial?: PublicTrackHistory[],
) => {
  if (
    newMessage.is_ad ||
    newMessage.is_show_title ||
    ContextsToIgnoreForTrackHistory.includes(newMessage.context) ||
    initial?.some((item) => item.id === newMessage.id)
  ) {
    return false;
  }

  return meetsConfidenceThreshold(newMessage, recognitionConfidenceThreshold);
};

export const useRealtimeTrackHistory = () => {
  const confidenceScore = useGlobalContextStore(
    (s) => s.getConfiguration('acr_cloud_minimum_confidence_score')?.parameter_number,
  );
  const mostRecentTrackHistory = useTrackStore(
    (s) => (s.recentTrackHistory ?? []).slice(0, 10),
    (a, b) => head(a)?.id === head(b)?.id,
  );
  const clearTrackHistoryForShowId = useTrackStore((s) => s.clearShowHistoryById);

  const currentlyPlaying = useTrackStore(
    (s) => s.currentlyPlaying,
    (a, b) => a?.id === b?.id,
  );
  const fetchCurrentlyPlaying = useTrackStore((s) => s.fetchCurrentlyPlaying);
  const fetchMostRecentTrackHistory = useTrackStore((s) => s.fetchMostRecentTrackHistory);
  const mostRecentTrackHistoryFetching = useTrackStore((s) => s.loading.mostRecentTrackHistory);

  useEffect(() => {
    void fetchCurrentlyPlaying();
    void fetchMostRecentTrackHistory();
  }, [fetchCurrentlyPlaying, fetchMostRecentTrackHistory]);

  const loading = isNil(currentlyPlaying);
  useEffect(() => {
    if (isNil(currentlyPlaying) || isNil(confidenceScore)) {
      return;
    }
    if (shouldPlaceInTrackHistory(currentlyPlaying, confidenceScore, mostRecentTrackHistory)) {
      void fetchMostRecentTrackHistory();
      if (
        currentlyPlaying.airing_show_context === 'live' &&
        isNonNil(currentlyPlaying.show_history_id)
      ) {
        clearTrackHistoryForShowId(currentlyPlaying.show_history_id);
      }
    }
  }, [
    clearTrackHistoryForShowId,
    confidenceScore,
    currentlyPlaying,
    fetchMostRecentTrackHistory,
    mostRecentTrackHistory,
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      void fetchCurrentlyPlaying();
    }, 10_000);

    return () => clearInterval(interval);
  }, [confidenceScore, fetchCurrentlyPlaying]);

  return useMemo(
    () => ({
      currentlyPlaying,
      initialCurrentlyPlayignLoading: loading,
      mostRecentTrackHistory,
      mostRecentTrackHistoryLoading: mostRecentTrackHistoryFetching,
    }),
    [currentlyPlaying, loading, mostRecentTrackHistory, mostRecentTrackHistoryFetching],
  );
};

export const useManageTracks = () => {
  useRealtimeTrackHistory();
  const me = useUserStore((s) => s.me);
  const subscriptionsFetching = useTrackStore((s) => s.loading.subscriptions);
  const fetchTrackSubscriptions = useTrackStore((s) => s.fetchTrackSubscriptions);
  const subscriptionsLoaded = useTrackStore((s) => isNonNil(s.trackSubscriptionsById));

  useEffect(() => {
    if (subscriptionsFetching || subscriptionsLoaded) {
      return;
    }

    void fetchTrackSubscriptions(me);
  }, [fetchTrackSubscriptions, me, subscriptionsFetching, subscriptionsLoaded]);

  const currentlyPlayingFetched = useTrackStore((s) => isNonNil(s.currentlyPlaying));
  const mostRecentTrackHistoryFetched = useTrackStore((s) => isNonNil(s.recentTrackHistory));
  const loadingInitial = useTrackStore((s) => s.loading.initial);
  const setInitialLoadCompleted = useTrackStore((s) => s.setInitialLoadCompleted);
  useEffect(() => {
    if (!loadingInitial) {
      return;
    }
    if (currentlyPlayingFetched && mostRecentTrackHistoryFetched && subscriptionsLoaded) {
      setInitialLoadCompleted();
    }
  }, [
    currentlyPlayingFetched,
    loadingInitial,
    mostRecentTrackHistoryFetched,
    setInitialLoadCompleted,
    subscriptionsLoaded,
  ]);
};
